/**
 * @file useMessageNotifications.js
 * @brief Handles browser notifications for incoming messages and transaction updates.
 */
import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getUserDisplayName } from "../utils/userDisplayName";

/**
 * Create notification helpers for chat messages and transaction events.
 * @param {Object} params Hook parameters.
 * @param {boolean} params.enabled Whether notifications should be active for the current session.
 * @param {string} params.pathname Current route path used to suppress notifications for the active thread.
 * @returns {Object} Notification helpers for chat-related events.
 */
export function useMessageNotifications({ enabled, pathname }) {
    const { t } = useTranslation();
    const notifiedMessageIdsRef = useRef(new Set());
    const warnedRef = useRef(false);
    const windowNotificationsRef = useRef(new Map());

    const notificationTag = useCallback((listingId, otherUserId) => {
        return `chat-${listingId}-${otherUserId}`;
    }, []);

    const getActiveThreadFromPath = useCallback((path) => {
        if (!path) return null;
        const clean = path.replace(/\/+$/, "");
        const parts = clean.split("/").filter(Boolean);
        if (parts.length !== 3 || parts[0] !== "messages") return null;
        return { listingId: parts[1], otherUserId: parts[2] };
    }, []);

    const canUseNotifications = useCallback(() => {
        if (typeof window === "undefined") return false;
        if (!("Notification" in window)) {
            if (!warnedRef.current) {
                console.warn(t("notifications.unsupported"));
                warnedRef.current = true;
            }
            return false;
        }
        if (!window.isSecureContext) {
            if (!warnedRef.current) {
                console.warn(t("notifications.httpsRequired"));
                warnedRef.current = true;
            }
            return false;
        }
        return true;
    }, [t]);

    const requestNotificationPermission = useCallback(async () => {
        if (!enabled) return "denied";
        if (!canUseNotifications()) return "denied";

        if (Notification.permission !== "default") {
            return Notification.permission;
        }

        try {
            return await Notification.requestPermission();
        } catch {
            return "denied";
        }
    }, [canUseNotifications, enabled]);

    const closeNotificationsByTag = useCallback((tag) => {
        const winNotif = windowNotificationsRef.current.get(tag);
        if (winNotif) {
            try { winNotif.close(); } catch {}
            windowNotificationsRef.current.delete(tag);
        }

        if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
            navigator.serviceWorker.ready
                .then((registration) => {
                    registration?.active?.postMessage({ type: "CLEAR_CHAT_NOTIFICATION", tag });
                    return navigator.serviceWorker.getRegistrations();
                })
                .then((registrations) => Promise.all(
                    registrations.map((registration) =>
                        registration.getNotifications({ tag }).then((notifications) => {
                            notifications.forEach((n) => n.close());
                        })
                    )
                ))
                .catch(() => {});
        }
    }, []);

    const clearConversationNotification = useCallback((listingId, otherUserId) => {
        if (!listingId || !otherUserId) return;
        closeNotificationsByTag(notificationTag(listingId, otherUserId));
    }, [closeNotificationsByTag, notificationTag]);

    const notifyIncomingMessage = useCallback((message, { listingId, otherUserId, fallbackName }) => {
        if (!canUseNotifications()) return;
        if (Notification.permission !== "granted") return;
        if (!message?.id) return;

        const id = String(message.id);
        if (notifiedMessageIdsRef.current.has(id)) return;
        notifiedMessageIdsRef.current.add(id);

        if (notifiedMessageIdsRef.current.size > 200) {
            const oldest = notifiedMessageIdsRef.current.values().next().value;
            notifiedMessageIdsRef.current.delete(oldest);
        }

        const activeThread = getActiveThreadFromPath(pathname);
        const isActiveThread =
            activeThread &&
            String(activeThread.listingId) === String(listingId) &&
            String(activeThread.otherUserId) === String(otherUserId);

        if (isActiveThread && document.visibilityState === "visible") return;

        const senderName = getUserDisplayName(message.sender?.user, fallbackName || t("notifications.someone")).primaryName;
        const body = message.content?.trim() || t("notifications.attachment");

        const title = t("notifications.newMessageFrom", { name: senderName });
        const targetUrl = `/messages/${listingId}/${otherUserId}`;
        const tag = notificationTag(listingId, otherUserId);
        const options = {
            body,
            tag,
            renotify: true,
            data: { url: targetUrl },
        };

        // Chrome on Android is more reliable with ServiceWorkerRegistration.showNotification.
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistration()
                .then((registration) => {
                    if (registration) {
                        return registration.showNotification(title, options);
                    }

                    const notification = new Notification(title, options);
                    windowNotificationsRef.current.set(tag, notification);
                    notification.onclick = () => {
                        window.focus();
                        window.location.assign(targetUrl);
                        notification.close();
                        windowNotificationsRef.current.delete(tag);
                    };
                    notification.onclose = () => {
                        windowNotificationsRef.current.delete(tag);
                    };
                    return null;
                })
                .catch(() => {
                    const notification = new Notification(title, options);
                    windowNotificationsRef.current.set(tag, notification);
                    notification.onclick = () => {
                        window.focus();
                        window.location.assign(targetUrl);
                        notification.close();
                        windowNotificationsRef.current.delete(tag);
                    };
                    notification.onclose = () => {
                        windowNotificationsRef.current.delete(tag);
                    };
                });
            return;
        }

        const notification = new Notification(title, options);
        windowNotificationsRef.current.set(tag, notification);
        notification.onclick = () => {
            window.focus();
            window.location.assign(targetUrl);
            notification.close();
            windowNotificationsRef.current.delete(tag);
        };
        notification.onclose = () => {
            windowNotificationsRef.current.delete(tag);
        };
    }, [canUseNotifications, getActiveThreadFromPath, notificationTag, pathname, t]);

    const notifyIncomingTransaction = useCallback((transactionEvent, { listingId, otherUserId, fallbackName }) => {
        if (!canUseNotifications()) return;
        if (Notification.permission !== "granted") return;

        const activeThread = getActiveThreadFromPath(pathname);
        const isActiveThread =
            activeThread &&
            String(activeThread.listingId) === String(listingId) &&
            String(activeThread.otherUserId) === String(otherUserId);

        if (isActiveThread && document.visibilityState === "visible") return;

        const actorName = fallbackName || t("notifications.someone");
        const tx = transactionEvent?.transaction;
        const status = tx?.status ? t(`messages.transaction.statuses.${tx.status}`) : t("notifications.transactionUpdated");

        const title = t("notifications.transactionTitle", { name: actorName });
        const body = t("notifications.transactionBody", { status });
        const targetUrl = `/messages/${listingId}/${otherUserId}`;
        const tag = notificationTag(listingId, otherUserId);
        const options = {
            body,
            tag,
            renotify: true,
            data: { url: targetUrl },
        };

        /**
         * Show the notification with the window API when the service worker path is unavailable.
         * @returns {void}
         */
        const fallbackShow = () => {
            const notification = new Notification(title, options);
            windowNotificationsRef.current.set(tag, notification);
            notification.onclick = () => {
                window.focus();
                window.location.assign(targetUrl);
                notification.close();
                windowNotificationsRef.current.delete(tag);
            };
            notification.onclose = () => {
                windowNotificationsRef.current.delete(tag);
            };
        };

        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistration()
                .then((registration) => {
                    if (registration) {
                        return registration.showNotification(title, options);
                    }
                    fallbackShow();
                    return null;
                })
                .catch(() => {
                    fallbackShow();
                });
            return;
        }

        fallbackShow();
    }, [canUseNotifications, getActiveThreadFromPath, notificationTag, pathname, t]);

    useEffect(() => {
        if (!enabled) return;
        if (!canUseNotifications()) return;
        if (Notification.permission !== "default") return;

        /**
         * Request notification permission after the user's first interaction.
         * @returns {void}
         */
        const requestPermission = () => {
            requestNotificationPermission().catch(() => {});
        };

        window.addEventListener("pointerdown", requestPermission, { once: true });
        window.addEventListener("touchstart", requestPermission, { once: true });
        window.addEventListener("click", requestPermission, { once: true });
        window.addEventListener("keydown", requestPermission, { once: true });

        return () => {
            window.removeEventListener("pointerdown", requestPermission);
            window.removeEventListener("touchstart", requestPermission);
            window.removeEventListener("click", requestPermission);
            window.removeEventListener("keydown", requestPermission);
        };
    }, [canUseNotifications, enabled, requestNotificationPermission]);

    useEffect(() => {
        const activeThread = getActiveThreadFromPath(pathname);
        if (!activeThread) return;
        clearConversationNotification(activeThread.listingId, activeThread.otherUserId);
    }, [clearConversationNotification, getActiveThreadFromPath, pathname]);

    return {
        notifyIncomingMessage,
        notifyIncomingTransaction,
        requestNotificationPermission,
        clearConversationNotification,
    };
}
